"use client";

import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  RadialBarChart,
  RadialBar,
  Legend,
} from "recharts";
import {
  Music,
  Sparkles,
  TrendingUp,
  AlertCircle,
  Heart,
  ArrowRight,
  Waves,
  Zap,
  Loader2,
  Download,
  Share2,
  Copy,
  MessageCircle,
  Instagram,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import type { MusicDNAResult } from "@/lib/music-dna/analyzeMusicDNA";
import Link from "next/link";
import { Button } from "@/components/ui/Button";
import toast from "react-hot-toast";
import { TonyAvatar } from "./TonyAvatar";
import { MusicDNAAnimation } from "./MusicDNAAnimation";

// 공유 버튼 컴포넌트
function ShareButtons({ 
  musicDNA, 
  overallScore 
}: { 
  musicDNA: MusicDNAResult;
  overallScore: number;
}) {
  const shareText = `🎵 내 Music DNA는 "${musicDNA.title}" ${musicDNA.emoji}\n\n${musicDNA.description}\n\n나도 분석받아보기 👉`;
  const shareUrl = typeof window !== 'undefined' ? window.location.origin + '/music-analysis/analyze' : 'https://connect-tone.com/music-analysis/analyze';

  // 카카오톡 공유
  const handleKakaoShare = () => {
    if (typeof window !== 'undefined' && (window as any).Kakao) {
      const Kakao = (window as any).Kakao;
      if (!Kakao.isInitialized()) {
        // 카카오 SDK 초기화가 안 되어 있으면 URL 복사로 대체
        handleCopyLink();
        return;
      }
      
      Kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title: `🎵 내 Music DNA: ${musicDNA.title} ${musicDNA.emoji}`,
          description: musicDNA.description,
          imageUrl: 'https://connect-tone.com/AI_result.png',
          link: {
            mobileWebUrl: shareUrl,
            webUrl: shareUrl,
          },
        },
        buttons: [
          {
            title: '나도 분석받기',
            link: {
              mobileWebUrl: shareUrl,
              webUrl: shareUrl,
            },
          },
        ],
      });
    } else {
      // 카카오 SDK가 없으면 URL 복사로 대체
      handleCopyLink();
    }
  };

  // 인스타그램 스토리 공유 (모바일만)
  const handleInstagramShare = async () => {
    const text = `🎵 내 Music DNA는 "${musicDNA.title}" ${musicDNA.emoji}\n\n${musicDNA.tagline}\n\n커넥톤에서 나도 분석받아보기!`;
    
    // 모바일에서 공유 API 사용
    if (navigator.share) {
      try {
        await navigator.share({
          title: `내 Music DNA: ${musicDNA.title}`,
          text: text,
          url: shareUrl,
        });
      } catch (err) {
        // 사용자가 취소한 경우 무시
        console.log('Share cancelled');
      }
    } else {
      // 데스크탑에서는 클립보드에 복사
      handleCopyLink();
    }
  };

  // 링크 복사
  const handleCopyLink = async () => {
    const textToCopy = `${shareText}\n${shareUrl}`;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      toast.success('공유 링크가 복사되었어요! 📋', {
        duration: 2000,
        icon: '✨',
      });
    } catch (err) {
      // 클립보드 API 실패 시 대체 방법
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('공유 링크가 복사되었어요! 📋', {
        duration: 2000,
        icon: '✨',
      });
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-white/50 mr-1">공유하기</span>
      
      {/* 카카오톡 공유 */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleKakaoShare}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FEE500] text-[#3C1E1E] text-xs font-semibold shadow-lg hover:shadow-xl transition-all"
        title="카카오톡으로 공유"
      >
        <MessageCircle className="w-3.5 h-3.5" />
        카카오톡
      </motion.button>

      {/* 인스타그램/일반 공유 */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleInstagramShare}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] text-white text-xs font-semibold shadow-lg hover:shadow-xl transition-all"
        title="공유하기"
      >
        <Share2 className="w-3.5 h-3.5" />
        공유
      </motion.button>

      {/* 링크 복사 */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleCopyLink}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 text-white text-xs font-medium hover:bg-white/30 transition-all"
        title="링크 복사"
      >
        <Copy className="w-3.5 h-3.5" />
        복사
      </motion.button>
    </div>
  );
}

// 사용자 아바타 컴포넌트
function UserAvatar({ image, displayName }: { image?: string | null; displayName?: string | null }) {
  const [imageError, setImageError] = useState(false);

  if (image && !imageError) {
    return (
      <div className="relative w-8 h-8 flex-shrink-0">
        <Image
          src={image}
          alt={displayName || "사용자"}
          fill
          className="rounded-full border-2 border-purple-300 object-cover"
          unoptimized
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  return (
    <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0">
      <span className="text-purple-600 text-sm font-bold">
        {displayName?.[0]?.toUpperCase() || "U"}
      </span>
    </div>
  );
}

// 채팅용 타자 효과 컴포넌트
function TypingChatText({ 
  text, 
  onTypingStart,
  onTypingComplete 
}: { 
  text: string;
  onTypingStart?: () => void;
  onTypingComplete?: () => void;
}) {
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const hasCompletedRef = useRef(false);
  const textRef = useRef(text);
  const onStartRef = useRef(onTypingStart);
  const onCompleteRef = useRef(onTypingComplete);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasStartedRef = useRef(false);

  // 콜백을 ref에 저장
  useEffect(() => {
    onStartRef.current = onTypingStart;
    onCompleteRef.current = onTypingComplete;
  }, [onTypingStart, onTypingComplete]);

  useEffect(() => {
    // 텍스트가 변경되었으면 초기화
    if (textRef.current !== text) {
      textRef.current = text;
      hasCompletedRef.current = false;
      hasStartedRef.current = false;
      // 기존 인터벌 정리
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // 이미 완료된 경우, 전체 텍스트를 표시하고 다시 시작하지 않음
    if (hasCompletedRef.current && textRef.current === text) {
      setDisplayedText(text);
      setIsTyping(false);
      return;
    }

    // 이미 실행 중이면 다시 시작하지 않음
    if (intervalRef.current) {
      return;
    }

    // 타자 효과 시작
    setDisplayedText("");
    setIsTyping(true);
    let currentIndex = 0;
    const targetText = text;

    // 시작 콜백 호출
    if (!hasStartedRef.current && onStartRef.current) {
      hasStartedRef.current = true;
      onStartRef.current();
    }

    intervalRef.current = setInterval(() => {
      if (currentIndex < targetText.length) {
        setDisplayedText(targetText.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        setIsTyping(false);
        hasCompletedRef.current = true;
        hasStartedRef.current = false; // 다음 메시지를 위해 초기화
        setDisplayedText(targetText); // 전체 텍스트 표시
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        // 타자 효과 완료 시 콜백 호출
        if (onCompleteRef.current) {
          setTimeout(() => {
            if (onCompleteRef.current) {
              onCompleteRef.current();
            }
          }, 300); // 약간의 딜레이 후
        }
      }
    }, 30);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [text]);

  return (
    <p className="text-sm sm:text-base whitespace-pre-wrap">
      {displayedText}
      {isTyping && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.8, repeat: Infinity }}
          className="inline-block w-2 h-4 bg-purple-500 ml-1 align-middle"
        />
      )}
    </p>
  );
}

// 요약 말풍선 래퍼 컴포넌트 (완료 상태 관리)
function SummaryBubble({
  summaryText,
  summaryIdRef,
  summaryCompletedRef,
  summaryCompleted,
  onComplete,
}: {
  summaryText: string;
  summaryIdRef: React.MutableRefObject<string>;
  summaryCompletedRef: React.MutableRefObject<boolean>;
  summaryCompleted: boolean;
  onComplete: () => void;
}) {
  // 완료 상태 확인 (ref 우선, 재렌더링에도 안정적)
  const isCompleted = summaryCompletedRef.current || summaryCompleted;
  
  // 완료된 경우 StaticBubble만 렌더링
  if (isCompleted) {
    return <StaticBubble key={`summary-static-${summaryIdRef.current}`} text={summaryText} />;
  }
  
  // 완료되지 않은 경우에만 TypingBubble 렌더링
  return (
    <TypingBubble 
      key={`summary-typing-${summaryIdRef.current}`}
      text={summaryText} 
      delay={0.2}
      onComplete={onComplete}
    />
  );
}

// 고정 말풍선 컴포넌트 (타자 효과 없음)
function StaticBubble({ text }: { text: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="relative"
    >
      <div className="flex items-start gap-3">
        {/* 토니 캐릭터 */}
        <TonyAvatar size="md" />
        
        {/* 말풍선 */}
        <div className="relative bg-white rounded-2xl rounded-tl-none shadow-lg border-2 border-purple-200 p-4 sm:p-5 max-w-[85%] sm:max-w-md">
          {/* 말풍선 꼬리 */}
          <div className="absolute -left-2 top-0 w-0 h-0 border-t-[12px] border-t-transparent border-r-[12px] border-r-purple-200" />
          <div className="absolute -left-1 top-0.5 w-0 h-0 border-t-[10px] border-t-transparent border-r-[10px] border-r-white" />
          
          <p className="text-gray-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
            {text}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// 타자 효과 말풍선 컴포넌트
function TypingBubble({ 
  text, 
  delay = 0,
  onComplete
}: { 
  text: string; 
  delay?: number;
  onComplete?: () => void;
}) {
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = useRef(onComplete);
  const isMountedRef = useRef(true);
  const hasCompletedRef = useRef(false);
  const textRef = useRef(text);

  // onComplete 콜백을 ref에 저장 (dependency 문제 방지)
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!text || text.trim() === "") {
      setDisplayedText("");
      setIsTyping(false);
      return;
    }
    
    // 텍스트가 변경되었으면 완료 상태 초기화
    if (textRef.current !== text) {
      textRef.current = text;
      hasCompletedRef.current = false;
    }
    
    // 이미 완료된 경우, 전체 텍스트를 표시하고 다시 시작하지 않음
    if (hasCompletedRef.current) {
      setDisplayedText(text);
      setIsTyping(false);
      return;
    }
    
    // 이미 실행 중이면 다시 시작하지 않음
    if (timerRef.current || intervalRef.current) {
      return;
    }
    
    isMountedRef.current = true;
    
    // 타자 효과 시작
    setDisplayedText("");
    setIsTyping(false);
    
    const targetText = text;
    
    timerRef.current = setTimeout(() => {
      if (!isMountedRef.current || hasCompletedRef.current) {
        return;
      }
      
      setIsTyping(true);
      let currentIndex = 0;
      
      intervalRef.current = setInterval(() => {
        if (!isMountedRef.current || hasCompletedRef.current) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return;
        }
        
        if (currentIndex < targetText.length) {
          setDisplayedText(targetText.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          setIsTyping(false);
          hasCompletedRef.current = true;
          setDisplayedText(targetText); // 전체 텍스트 표시
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          // 완료 콜백 호출
          if (onCompleteRef.current) {
            onCompleteRef.current();
          }
        }
      }, 30); // 타자 속도 (ms)
    }, delay);

    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [text, delay]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, delay }}
      className="relative"
    >
      <div className="flex items-start gap-3">
        {/* 토니 캐릭터 */}
        <TonyAvatar size="md" />
        
        {/* 말풍선 */}
        <div className="relative bg-white rounded-2xl rounded-tl-none shadow-lg border-2 border-purple-200 p-4 sm:p-5 max-w-[85%] sm:max-w-md">
          {/* 말풍선 꼬리 */}
          <div className="absolute -left-2 top-0 w-0 h-0 border-t-[12px] border-t-transparent border-r-[12px] border-r-purple-200" />
          <div className="absolute -left-1 top-0.5 w-0 h-0 border-t-[10px] border-t-transparent border-r-[10px] border-r-white" />
          
          <p className="text-gray-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
            {displayedText}
            {isTyping && (
              <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.8, repeat: Infinity }}
                className="inline-block w-2 h-4 bg-purple-500 ml-1 align-middle"
              />
            )}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export interface SimilarArtistRecommendation {
  artist: string;
  genre: string;
  similarity: number;
  spotifyArtistId?: string;
  externalUrl?: string;
}

export interface VocalAnalysisResult {
  pitchAccuracy: number; // 0-100
  rhythmStability: number; // 0-100
  energyDynamics: number; // 0-100
  emotionalExpression: number; // 0-100
  toneConsistency: number; // 0-100
  overallScore: number; // 0-100 (평균)
  comments: {
    pitch: string;
    rhythm: string;
    energy: string;
    emotion: string;
    tone: string;
    summary: string;
    coachComment?: string; // 감성적 AI 코치 코멘트
    nextStep?: string; // 다음 단계 추천
  };
  recommendations: Array<{
    title: string;
    description: string;
    link?: string;
    practiceSongs?: string[];
  }>;
  analysisDetails?: {
    spotify?: {
      trackId: string;
      title: string;
      artist: string;
      album: string;
      albumArt: string;
      popularity: number;
      releaseDate: string;
      previewUrl?: string | null;
      explicit?: boolean;
      durationMs?: number;
      spotifyArtistId?: string;
      artistPopularity?: number;
      artistFollowers?: number;
      genres?: string[];
      similarArtists?: SimilarArtistRecommendation[];
      spotifyRecommendations?: Array<{
        trackId: string;
        title: string;
        artist: string;
        album: string;
        albumArt: string;
        previewUrl?: string | null;
        externalUrl: string;
      }>;
    };
  };
  musicDNA?: MusicDNAResult;
}

interface VocalAnalysisReportProps {
  result: VocalAnalysisResult;
  tier: "free" | "advanced";
  chatMessages?: Array<{ role: "user" | "tony"; message: string }>;
  setChatMessages?: React.Dispatch<React.SetStateAction<Array<{ role: "user" | "tony"; message: string }>>>;
  chatInput?: string;
  setChatInput?: React.Dispatch<React.SetStateAction<string>>;
  isChatting?: boolean;
  setIsChatting?: React.Dispatch<React.SetStateAction<boolean>>;
  user?: { uid: string; displayName?: string | null; image?: string | null; profileImage?: string | null } | null;
  chatCount?: number;
  setChatCount?: React.Dispatch<React.SetStateAction<number>>;
  maxFreeChats?: number;
  chatPointCost?: number; // 3회부터 차감되는 포인트
  onAdvancedAnalysis?: () => void;
  router?: any;
  audioUrl?: string | null;
  isPlaying?: boolean;
}

export default function VocalAnalysisReport({
  result,
  tier,
  chatMessages = [],
  setChatMessages,
  chatInput = "",
  setChatInput,
  isChatting = false,
  setIsChatting,
  user,
  chatCount = 0,
  setChatCount,
  maxFreeChats = Number.MAX_SAFE_INTEGER,
  chatPointCost = 0,
  onAdvancedAnalysis,
  router,
  audioUrl,
  isPlaying = false,
}: VocalAnalysisReportProps) {
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const [summaryCompleted, setSummaryCompleted] = useState(false);
  const summaryTextRef = useRef<string>("");
  const summaryCompletedRef = useRef(false);
  const summaryIdRef = useRef<string>("");
  const [lastTonyMessageTyping, setLastTonyMessageTyping] = useState(false);
  const completedTonyMessagesRef = useRef<Set<string>>(new Set()); // 메시지 내용을 키로 사용

  // 완료 핸들러를 useCallback으로 메모이제이션
  const handleSummaryComplete = useCallback(() => {
    summaryCompletedRef.current = true;
    setSummaryCompleted(true);
  }, []);

  // summary 텍스트가 변경되면 완료 상태 초기화
  useEffect(() => {
    const currentSummary = result.comments.summary || "분석 중...";
    const currentSummaryId = `${result.comments.summary || ""}`;
    
    // 텍스트 ID가 변경된 경우에만 초기화
    if (summaryIdRef.current && summaryIdRef.current !== currentSummaryId) {
      summaryIdRef.current = currentSummaryId;
      summaryTextRef.current = currentSummary;
      // 텍스트가 변경되었으므로 완료 상태 초기화
      summaryCompletedRef.current = false;
      setSummaryCompleted(false);
    } else if (!summaryIdRef.current) {
      // 처음 로드되는 경우
      summaryIdRef.current = currentSummaryId;
      summaryTextRef.current = currentSummary;
      // 초기 상태는 완료되지 않음
      summaryCompletedRef.current = false;
    }
    // 텍스트가 변경되지 않았으면 완료 상태 유지
  }, [result.comments.summary]);

  // Radar Chart 데이터 구성
  const radarData = [
    {
      category: "음정 정확도",
      score: result.pitchAccuracy,
      fullMark: 100,
    },
    {
      category: "리듬 안정성",
      score: result.rhythmStability,
      fullMark: 100,
    },
    {
      category: "에너지 밸런스",
      score: result.energyDynamics,
      fullMark: 100,
    },
    {
      category: "감정 표현력",
      score: result.emotionalExpression,
      fullMark: 100,
    },
    {
      category: "톤 일관성",
      score: result.toneConsistency,
      fullMark: 100,
    },
  ];

  // Bar Chart 데이터 구성
  const barData = [
    { name: "음정", score: result.pitchAccuracy },
    { name: "리듬", score: result.rhythmStability },
    { name: "에너지", score: result.energyDynamics },
    { name: "감정", score: result.emotionalExpression },
    { name: "톤", score: result.toneConsistency },
  ];

  // 점수에 따른 색상 결정
  const getScoreColor = (score: number) => {
    if (score >= 85) return "#66E6C3"; // 민트 (우수)
    if (score >= 70) return "#7E57C2"; // 보라 (양호)
    if (score >= 50) return "#FFC107"; // 노랑 (보통)
    return "#FF5252"; // 빨강 (개선 필요)
  };

  // 총점 게이지 색상
  const getOverallColor = (score: number) => {
    if (score >= 85) return "#66E6C3";
    if (score >= 70) return "#7E57C2";
    if (score >= 50) return "#FFC107";
    return "#FF5252";
  };

  const spotifyDetails = result.analysisDetails?.spotify;
  const similarArtists = spotifyDetails?.similarArtists || [];

  const sanitizeMetricComment = (comment?: string) => {
    if (!comment) return "";
    return comment.replace(/^\s*\d+\s*점\s*-\s*/u, "").trim();
  };

  const [isDNARevealed, setIsDNARevealed] = useState(false);

  useEffect(() => {
    if (!result.musicDNA) {
      setIsDNARevealed(false);
      return;
    }

    setIsDNARevealed(false);
    const timer = window.setTimeout(() => {
      setIsDNARevealed(true);
    }, 900);

    return () => {
      window.clearTimeout(timer);
    };
  }, [result.musicDNA?.key]);

  const attributeCards = [
    {
      key: "pitch",
      title: "음정 정확도",
      score: result.pitchAccuracy,
      comment: sanitizeMetricComment(result.comments.pitch),
      delay: 0.25,
    },
    {
      key: "rhythm",
      title: "리듬 안정성",
      score: result.rhythmStability,
      comment: sanitizeMetricComment(result.comments.rhythm),
      delay: 0.3,
    },
    {
      key: "energy",
      title: "에너지 밸런스",
      score: result.energyDynamics,
      comment: sanitizeMetricComment(result.comments.energy),
      delay: 0.35,
    },
    {
      key: "emotion",
      title: "감정 표현력",
      score: result.emotionalExpression,
      comment: sanitizeMetricComment(result.comments.emotion),
      delay: 0.4,
    },
    {
      key: "tone",
      title: "톤 일관성",
      score: result.toneConsistency,
      comment: sanitizeMetricComment(result.comments.tone),
      delay: 0.45,
    },
  ];

  // 오늘의 핵심 개선 포인트: 가장 낮은 점수 항목
  const focusAreas = [
    { key: "pitch", label: "음정 정확도", score: result.pitchAccuracy },
    { key: "rhythm", label: "리듬 안정성", score: result.rhythmStability },
    { key: "energy", label: "에너지 밸런스", score: result.energyDynamics },
    { key: "emotion", label: "감정 표현력", score: result.emotionalExpression },
    { key: "tone", label: "톤 일관성", score: result.toneConsistency },
  ];

  const lowestFocusArea = focusAreas.reduce(
    (lowest, current) => (current.score < lowest.score ? current : lowest),
    focusAreas[0]
  );

  const topStrengthAreas = useMemo(() => {
    const sorted = [...focusAreas].sort((a, b) => b.score - a.score);
    const strong = sorted.filter(item => item.score >= 60);
    return (strong.length > 0 ? strong : sorted).slice(0, 2);
  }, [focusAreas]);

  const summaryLead = useMemo(() => {
    const summary = result.comments.summary?.trim() || "";
    if (!summary) return "";
    const firstSentence = summary.match(/^[^.!?\n]+[.!?]?/)?.[0]?.trim();
    return firstSentence || summary.slice(0, 120);
  }, [result.comments.summary]);

  const primaryPracticeTip = result.recommendations[0];

  const [fullDetailsOpen, setFullDetailsOpen] = useState(true);
  const [showUpgradeCta, setShowUpgradeCta] = useState(false);

  useEffect(() => {
    if (tier !== "free") {
      setShowUpgradeCta(false);
      return;
    }
    if (!summaryCompleted) return;
    const timer = window.setTimeout(() => setShowUpgradeCta(true), 2200);
    return () => window.clearTimeout(timer);
  }, [summaryCompleted, tier]);

  useEffect(() => {
    if (tier === "free" && chatCount > 0) {
      setShowUpgradeCta(true);
    }
  }, [chatCount, tier]);

  /** 히어로는 지표 스냅샷만 — 긴 문장 코멘트는 아래 코치 섹션에만 둬서 중복·비전문적 인상을 줄임 */
  const heroScoreBand = useMemo(() => {
    const s = Math.round(result.overallScore);
    if (s >= 85)
      return {
        label: "우수",
        chipClass: "bg-emerald-500/25 text-emerald-100 ring-1 ring-emerald-400/40",
      };
    if (s >= 70)
      return {
        label: "양호",
        chipClass: "bg-sky-500/25 text-sky-100 ring-1 ring-sky-400/35",
      };
    if (s >= 55)
      return {
        label: "보완",
        chipClass: "bg-amber-500/25 text-amber-100 ring-1 ring-amber-400/35",
      };
    return {
      label: "집중",
      chipClass: "bg-rose-500/25 text-rose-100 ring-1 ring-rose-400/35",
    };
  }, [result.overallScore]);

  const heroMetricStrip = useMemo(
    () => [
      { label: "음정", score: result.pitchAccuracy },
      { label: "리듬", score: result.rhythmStability },
      { label: "에너지", score: result.energyDynamics },
      { label: "감정", score: result.emotionalExpression },
      { label: "톤", score: result.toneConsistency },
    ],
    [
      result.pitchAccuracy,
      result.rhythmStability,
      result.energyDynamics,
      result.emotionalExpression,
      result.toneConsistency,
    ]
  );

  const topSimilarArtists = useMemo(() => {
    const list = result.analysisDetails?.spotify?.similarArtists;
    if (!list?.length) return [];
    return list.slice(0, 3);
  }, [result.analysisDetails?.spotify?.similarArtists]);

  return (
    <div className="space-y-8">
      {/* 상단 요약 히어로 — 한눈에 총점·요약·DNA */}
      <div className="relative overflow-hidden rounded-3xl border border-white/20 bg-gradient-to-br from-slate-900 via-violet-950 to-indigo-950 text-white shadow-2xl">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(168,85,247,0.35),transparent_55%)]" />
        <div className="relative z-10 flex flex-col gap-6 p-6 sm:flex-row sm:items-stretch sm:gap-8 sm:p-8">
          <div className="flex flex-1 flex-col items-center justify-center sm:min-w-[180px]">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-white/55 sm:text-[13px]">
              종합 점수
            </p>
            <div
              className="relative flex h-44 w-44 items-center justify-center sm:h-52 sm:w-52"
              style={{
                filter: "drop-shadow(0 16px 40px rgba(124,58,237,0.5))",
              }}
            >
              <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="44"
                  fill="none"
                  stroke="rgba(255,255,255,0.12)"
                  strokeWidth="10"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="44"
                  fill="none"
                  stroke={getOverallColor(result.overallScore)}
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={`${(result.overallScore / 100) * 276.46} 276.46`}
                />
              </svg>
              <span className="relative text-5xl font-black tabular-nums tracking-tight sm:text-6xl lg:text-7xl">
                {Math.round(result.overallScore)}
              </span>
            </div>
            <span
              className="mt-3 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
              style={{
                backgroundColor: "rgba(255,255,255,0.12)",
                color: getOverallColor(result.overallScore),
              }}
            >
              {tier === "advanced" ? "심화 분석" : "빠른 분석"}
            </span>
          </div>

          <div className="min-w-0 flex-1 space-y-4">
            <div>
              <div className="flex flex-wrap items-baseline gap-2 sm:gap-3">
                <h3 className="text-lg font-semibold tracking-tight text-white sm:text-xl">
                  이번 분석 요약
                </h3>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${heroScoreBand.chipClass}`}
                >
                  {heroScoreBand.label}
                </span>
              </div>
              <p className="mt-1.5 text-[11px] text-white/45 sm:text-xs">
                AI가 이번 녹음을 읽은 첫인상이에요
              </p>
              {summaryLead && (
                <p className="mt-3 text-sm leading-relaxed text-white/85 sm:text-base">
                  {summaryLead}
                </p>
              )}
              {topStrengthAreas.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {topStrengthAreas.map(item => (
                    <span
                      key={item.key}
                      className="inline-flex items-center rounded-full bg-emerald-500/20 px-2.5 py-1 text-[11px] font-medium text-emerald-100 ring-1 ring-emerald-400/30 sm:text-xs"
                    >
                      {item.label} {Math.round(item.score)}점
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 grid grid-cols-5 gap-1.5 sm:gap-2">
                {heroMetricStrip.map(row => (
                  <div
                    key={row.label}
                    className="rounded-lg bg-white/[0.07] px-1 py-2 text-center sm:px-2"
                  >
                    <div className="text-[10px] text-white/45 sm:text-[11px]">
                      {row.label}
                    </div>
                    <div className="mt-0.5 text-sm font-bold tabular-nums text-white sm:text-base">
                      {Math.round(row.score)}
                    </div>
                  </div>
                ))}
              </div>

              {topSimilarArtists.length > 0 && (
                <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2.5 sm:px-4">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-white/40">
                    참고 유사 스타일
                  </p>
                  <p className="mt-1 text-sm font-medium leading-snug text-white/90">
                    {topSimilarArtists.map(a => a.artist).join(" · ")}
                    {similarArtists.length > 3 ? " …" : ""}
                  </p>
                  <p className="mt-1.5 text-[10px] leading-relaxed text-white/40">
                    Spotify·라이브러리 메타데이터로 계산한 참고값이에요. 해당
                    아티스트와의 공식 연관·닮음을 보장하지 않습니다.
                  </p>
                </div>
              )}
            </div>

            {result.musicDNA && (
              <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-white/10 px-4 py-3 backdrop-blur-sm">
                <span className="text-3xl" aria-hidden>
                  {result.musicDNA.emoji}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-violet-200/90">
                    Music DNA
                  </p>
                  <p className="truncate font-semibold text-white">
                    {result.musicDNA.title}
                  </p>
                  <p className="truncate text-xs text-white/70">
                    {result.musicDNA.tagline}
                  </p>
                </div>
                <div className="w-full flex-shrink-0 pt-1 sm:w-auto sm:pt-0">
                  <ShareButtons
                    musicDNA={result.musicDNA}
                    overallScore={result.overallScore}
                  />
                </div>
              </div>
            )}

            {lowestFocusArea && (
              <p className="text-xs text-white/60">
                다음에 챙겨보면 좋은 포인트:{" "}
                <span className="font-semibold text-amber-200/95">
                  {lowestFocusArea.label}
                </span>
              </p>
            )}

            <button
              type="button"
              onClick={() => setFullDetailsOpen(v => !v)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/25 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15 sm:w-auto"
            >
              {fullDetailsOpen ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  요약만 보기 (차트·상세 접기)
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  상세 리포트 펼치기 (차트·토니 대화)
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {fullDetailsOpen && (
      <>
      {/* 종합 요약 섹션 - AI 점수 위로 이동 (무료 분석에만 표시) */}
      {tier !== "advanced" && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 sm:p-8 shadow-lg border border-gray-200"
      >
        {/* AI 토니가 분석한 나의 음악 */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
            AI 코치 토니 · 분석 코멘트
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            점수만으로는 안 보이는 부분을 토니가 풀어줄게요.
          </p>

          {/* 종합 코멘트 - 토니의 말풍선 */}
          <SummaryBubble
            summaryText={result.comments.summary || "분석 중..."}
            summaryIdRef={summaryIdRef}
            summaryCompletedRef={summaryCompletedRef}
            summaryCompleted={summaryCompleted}
            onComplete={handleSummaryComplete}
          />

          {/* 오늘의 핵심 개선 포인트 배지 */}
          {lowestFocusArea && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-purple-50 px-4 py-2 border border-purple-100">
              <span className="text-xs font-semibold text-purple-600">
                토니의 추천 포커스
              </span>
              <span className="text-xs sm:text-sm font-semibold text-purple-800">
                {lowestFocusArea.label}
              </span>
            </div>
          )}

          {tier === "free" && (topStrengthAreas.length > 0 || primaryPracticeTip) && (
            <div className="mt-5 rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/90 to-teal-50/50 p-4 sm:p-5">
              <p className="text-sm font-semibold text-emerald-900">
                이번 분석에서 도움이 될 만한 포인트
              </p>
              {topStrengthAreas.length > 0 && (
                <ul className="mt-3 space-y-2 text-sm text-gray-700">
                  {topStrengthAreas.map(item => (
                    <li key={item.key} className="flex gap-2">
                      <span className="text-emerald-600">✓</span>
                      <span>
                        <strong className="text-gray-900">{item.label}</strong>
                        {" "}쪽이 {Math.round(item.score)}점으로 안정적으로
                        잡혀 있어요. 이 느낌을 유지하면 좋아요.
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {primaryPracticeTip && (
                <div className="mt-4 rounded-xl border border-white/80 bg-white/90 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-purple-600">
                    오늘 해볼 연습
                  </p>
                  <p className="mt-1 font-semibold text-gray-900">
                    {primaryPracticeTip.title}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-gray-600">
                    {primaryPracticeTip.description}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* 토니와의 채팅 - 말풍선 바로 아래 (대화 영역은 리포트 카드와 시각적으로 구분) */}
          {setChatMessages && setChatInput && setIsChatting && (
            <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50/80 p-4 sm:p-5">
              <p className="mb-3 text-xs font-medium text-gray-500">
                토니에게 추가 질문
              </p>
              {/* 채팅 메시지 목록 */}
              <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                {chatMessages.length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    {(() => {
                      const isSummaryCompleted = summaryCompletedRef.current || summaryCompleted;
                      return isSummaryCompleted ? (
                        <p>토니에게 분석 결과에 대해 물어보세요! 💬</p>
                      ) : (
                        <p>토니가 분석을 말하고 있습니다... 잠시만 기다려주세요 ⏳</p>
                      );
                    })()}
                  </div>
                )}
                {chatMessages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex items-start gap-3 ${
                      msg.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    {msg.role === "tony" && (
                      <motion.div
                        animate={isPlaying ? {
                          scale: [1, 1.1, 1],
                          rotate: [0, 5, -5, 0],
                        } : {}}
                        transition={{
                          duration: 0.5,
                          repeat: isPlaying ? Infinity : 0,
                          ease: "easeInOut"
                        }}
                      >
                        <TonyAvatar size="sm" />
                      </motion.div>
                    )}
                    <div
                      className={`rounded-2xl px-4 py-3 max-w-[75%] ${
                        msg.role === "user"
                          ? "bg-purple-500 text-white rounded-tr-none"
                          : "bg-gray-100 text-gray-800 rounded-tl-none"
                      }`}
                    >
                      {msg.role === "tony" && idx === chatMessages.length - 1 && !completedTonyMessagesRef.current.has(msg.message) ? (
                        <TypingChatText 
                          key={`typing-${idx}-${msg.message.substring(0, 20)}`}
                          text={msg.message}
                          onTypingStart={() => {
                            // 타이핑 시작 시 입력 필드 비활성화
                            setLastTonyMessageTyping(true);
                          }}
                          onTypingComplete={() => {
                            // 타이핑 완료 시 완료된 메시지로 표시 (메시지 내용을 키로 사용)
                            completedTonyMessagesRef.current.add(msg.message);
                            // 타이핑 완료 시 입력 필드 활성화
                            setLastTonyMessageTyping(false);
                            // 마지막 토니 메시지 타이핑 완료 후, 채팅 횟수 제한에 도달했으면 안내 메시지 표시
                            if (chatCount >= (maxFreeChats || 1)) {
                              setShowLimitMessage(true);
                            }
                          }}
                        />
                      ) : (
                        <p className="text-sm sm:text-base whitespace-pre-wrap">
                          {msg.message}
                        </p>
                      )}
                    </div>
                    {msg.role === "user" && (
                      <UserAvatar 
                        image={user?.image || user?.profileImage}
                        displayName={user?.displayName}
                      />
                    )}
                  </motion.div>
                ))}
                {isChatting && (
                  <div className="flex items-start gap-3">
                    <motion.div
                      animate={isPlaying ? {
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, -5, 0],
                      } : {}}
                      transition={{
                        duration: 0.5,
                        repeat: isPlaying ? Infinity : 0,
                        ease: "easeInOut"
                      }}
                    >
                      <TonyAvatar size="sm" />
                    </motion.div>
                    <div className="bg-gray-100 rounded-2xl rounded-tl-none px-4 py-3">
                      <div className="flex gap-1">
                        <motion.div
                          animate={{ opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 1, repeat: Infinity }}
                          className="w-2 h-2 bg-gray-400 rounded-full"
                        />
                        <motion.div
                          animate={{ opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                          className="w-2 h-2 bg-gray-400 rounded-full"
                        />
                        <motion.div
                          animate={{ opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                          className="w-2 h-2 bg-gray-400 rounded-full"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 메시지 입력 */}
              {showLimitMessage && chatCount >= (maxFreeChats || 1) ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <TonyAvatar size="sm" />
                    <div className="flex-1">
                      <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-2">
                        토니와의 무료 대화를 모두 사용하셨네요! 🤔 더 깊이 있는 분석이 궁금하신가요?
                      </p>
                      <p className="text-purple-600 text-sm font-medium bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                        💬 토니와 대화를 이어가고 싶다면 <span className="font-bold text-purple-700">{chatPointCost}P</span>씩 차감되며 계속 대화할 수 있어요!
                      </p>
                    </div>
                  </div>
                  
                  {/* 토니와 대화 이어가기 입력창 - 위에 배치 */}
                  {setChatInput && setChatMessages && setIsChatting && (
                    <div className="mb-6">
                      <form
                        onSubmit={async (e) => {
                          e.preventDefault();
                          const isSummaryCompleted = summaryCompletedRef.current || summaryCompleted;
                          if (!chatInput?.trim() || isChatting || !isSummaryCompleted || lastTonyMessageTyping) return;

                          // 비회원이면 로그인 안내
                          if (!user?.uid) {
                            toast("로그인하면 토니와 대화할 수 있어요! 🔐", {
                              icon: "💬",
                              duration: 3000,
                            });
                            const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
                            setTimeout(() => {
                              window.location.href = `/auth/login?callbackUrl=${encodeURIComponent(currentPath)}`;
                            }, 1500);
                            return;
                          }

                          const userMessage = chatInput.trim();
                          const nextChatCount = (chatCount || 0) + 1;

                          // 유료 대화 - 포인트 확인 및 차감
                          if (user?.uid) {
                            try {
                              const { getPointBalance, spendPoints } = await import("@/lib/api/points");
                              const balance = await getPointBalance(user.uid);
                              
                              if (balance < chatPointCost) {
                                toast.error(`포인트가 부족합니다. (필요: ${chatPointCost}P, 보유: ${balance}P)`, {
                                  duration: 4000,
                                });
                                return;
                              }

                              // 포인트 차감 (알림 없이 조용히 차감)
                              const deductResult = await spendPoints(
                                user.uid,
                                chatPointCost,
                                "AI 대화"
                              );

                              if (!deductResult.success) {
                                toast.error(deductResult.error || "포인트 차감에 실패했습니다.");
                                return;
                              }

                              // 포인트 차감 성공 알림 제거 - 조용히 처리
                            } catch (error) {
                              console.error("포인트 차감 오류:", error);
                              toast.error("포인트 차감 중 오류가 발생했습니다.");
                              return;
                            }
                          }

                          if (setChatInput) setChatInput("");
                          if (setChatMessages) {
                            setChatMessages((prev) => [
                              ...prev,
                              { role: "user", message: userMessage },
                            ]);
                          }
                          if (setIsChatting) setIsChatting(true);
                          if (setChatCount) setChatCount(nextChatCount);

                          try {
                            const response = await fetch("/api/ai/tony-chat", {
                              method: "POST",
                              headers: {
                                "Content-Type": "application/json",
                              },
                              body: JSON.stringify({
                                message: userMessage,
                                analysisResult: result,
                              }),
                            });

                            const data = await response.json();

                            if (data.success && data.message && setChatMessages) {
                              setLastTonyMessageTyping(false);
                              setChatMessages((prev) => [
                                ...prev,
                                { role: "tony", message: data.message },
                              ]);
                            } else {
                              toast.error(data.error || "토니의 응답을 받을 수 없습니다.");
                              if (setChatCount) setChatCount((prev) => Math.max(0, prev - 1));
                            }
                          } catch (error) {
                            console.error("토니 채팅 오류:", error);
                            toast.error("토니와의 대화 중 오류가 발생했습니다.");
                            if (setChatCount) setChatCount((prev) => Math.max(0, prev - 1));
                          } finally {
                            if (setIsChatting) setIsChatting(false);
                          }
                        }}
                        className="flex items-center gap-2 w-full"
                      >
                        <input
                          type="text"
                          value={chatInput}
                          onChange={(e) => setChatInput?.(e.target.value)}
                          placeholder={(() => {
                            const isSummaryCompleted = summaryCompletedRef.current || summaryCompleted;
                            if (!isSummaryCompleted) {
                              return "토니가 말을 마칠 때까지 기다려주세요... ⏳";
                            }
                            if (lastTonyMessageTyping) {
                              return "토니가 답변하고 있습니다... 잠시만 기다려주세요 ⏳";
                            }
                            if (!user?.uid) {
                              return "토니에게 질문하세요... (로그인 필요) 💬";
                            }
                            return `토니에게 질문하세요... (${chatPointCost}P 차감) 💬`;
                          })()}
                          disabled={(() => {
                            const isSummaryCompleted = summaryCompletedRef.current || summaryCompleted;
                            return !isSummaryCompleted || isChatting || lastTonyMessageTyping;
                          })()}
                          className="flex-1 min-w-0 px-3 sm:px-4 py-3 rounded-xl border-2 border-purple-200 focus:outline-none focus:border-purple-400 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                        />
                        <Button
                          type="submit"
                          disabled={(() => {
                            const isSummaryCompleted = summaryCompletedRef.current || summaryCompleted;
                            return !chatInput?.trim() || !isSummaryCompleted || isChatting || lastTonyMessageTyping;
                          })()}
                          className="flex-shrink-0 px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isChatting ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            "전송"
                          )}
                        </Button>
                      </form>
                    </div>
                  )}
                  
                  {showUpgradeCta && (
                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm">
                      {onAdvancedAnalysis && (
                        <button
                          type="button"
                          onClick={() => onAdvancedAnalysis()}
                          className="font-medium text-purple-600 hover:text-purple-800 hover:underline"
                        >
                          심화 분석으로 더 알아보기 →
                        </button>
                      )}
                      <Link
                        href="/expert-feedback"
                        className="font-medium text-gray-600 hover:text-gray-900 hover:underline"
                      >
                        프로에게 물어보기 →
                      </Link>
                    </div>
                  )}
                </motion.div>
              ) : chatCount < (maxFreeChats || 2) || !user?.uid ? (
                <>
                  {/* 무료 대화 횟수 표시는 테스트 기간 동안 숨김 */}
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const isSummaryCompleted = summaryCompletedRef.current || summaryCompleted;
                      if (!chatInput?.trim() || isChatting || !isSummaryCompleted || lastTonyMessageTyping) return;

                      // 비회원이면 로그인 안내
                      if (!user?.uid) {
                        toast("로그인하면 토니와 대화할 수 있어요! 🔐", {
                          icon: "💬",
                          duration: 3000,
                        });
                        // 로그인 페이지로 이동 (선택사항)
                        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
                        setTimeout(() => {
                          window.location.href = `/auth/login?callbackUrl=${encodeURIComponent(currentPath)}`;
                        }, 1500);
                        return;
                      }

                      const userMessage = chatInput.trim();
                      const nextChatCount = (chatCount || 0) + 1;
                      const isFree = nextChatCount <= (maxFreeChats || 2);

                      // 유료 대화인 경우 포인트 확인 및 차감
                      if (!isFree && user?.uid) {
                        try {
                          const { getPointBalance, spendPoints } = await import("@/lib/api/points");
                          const balance = await getPointBalance(user.uid);
                          
                          if (balance < chatPointCost) {
                            toast.error(`포인트가 부족합니다. (필요: ${chatPointCost}P, 보유: ${balance}P)`, {
                              duration: 4000,
                            });
                            return;
                          }

                          // 포인트 차감 (시스템 알림은 표시하지 않고, 토스트로만 안내)
                          const deductResult = await spendPoints(
                            user.uid,
                            chatPointCost,
                            "AI 대화"
                          );

                          if (!deductResult.success) {
                            toast.error(deductResult.error || "포인트 차감에 실패했습니다.");
                            return;
                          }

                          // 포인트 차감 성공 알림 제거 - 조용히 처리
                        } catch (error) {
                          console.error("포인트 차감 오류:", error);
                          toast.error("포인트 차감 중 오류가 발생했습니다.");
                          return;
                        }
                      }

                      if (setChatInput) setChatInput("");
                      if (setChatMessages) {
                        setChatMessages((prev) => [
                          ...prev,
                          { role: "user", message: userMessage },
                        ]);
                      }
                      if (setIsChatting) setIsChatting(true);
                      if (setChatCount) setChatCount(nextChatCount);

                      try {
                        const response = await fetch("/api/ai/tony-chat", {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({
                            message: userMessage,
                            analysisResult: result,
                          }),
                        });

                        const data = await response.json();

                        if (data.success && data.message && setChatMessages) {
                          // 새 토니 메시지 추가 시 타이핑 상태 초기화
                          setLastTonyMessageTyping(false);
                          setChatMessages((prev) => [
                            ...prev,
                            { role: "tony", message: data.message },
                          ]);
                        } else {
                          toast.error(data.error || "토니의 응답을 받을 수 없습니다.");
                          if (setChatCount) setChatCount((prev) => Math.max(0, prev - 1));
                        }
                      } catch (error) {
                        console.error("토니 채팅 오류:", error);
                        toast.error("토니와의 대화 중 오류가 발생했습니다.");
                        if (setChatCount) setChatCount((prev) => Math.max(0, prev - 1));
                      } finally {
                        if (setIsChatting) setIsChatting(false);
                      }
                    }}
                    className="flex items-center gap-2 w-full"
                  >
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput?.(e.target.value)}
                      placeholder={(() => {
                        const isSummaryCompleted = summaryCompletedRef.current || summaryCompleted;
                        if (!isSummaryCompleted) {
                          return "토니가 말을 마칠 때까지 기다려주세요... ⏳";
                        }
                        if (lastTonyMessageTyping) {
                          return "토니가 답변하고 있습니다... 잠시만 기다려주세요 ⏳";
                        }
                        return "토니에게 질문하세요... 💬";
                      })()}
                      disabled={(() => {
                        const isSummaryCompleted = summaryCompletedRef.current || summaryCompleted;
                        return !isSummaryCompleted || isChatting || lastTonyMessageTyping;
                      })()}
                      className="flex-1 min-w-0 px-3 sm:px-4 py-3 rounded-xl border-2 border-gray-200 focus:outline-none focus:border-purple-400 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    />
                    <Button
                      type="submit"
                      disabled={(() => {
                        const isSummaryCompleted = summaryCompletedRef.current || summaryCompleted;
                        return !chatInput?.trim() || !isSummaryCompleted || isChatting || lastTonyMessageTyping;
                      })()}
                      className="flex-shrink-0 px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isChatting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        "전송"
                      )}
                    </Button>
                  </form>
                </>
              ) : null}
            </div>
          )}
        </div>

      </motion.div>
      )}

      {/* 무료: 항목별 코멘트 미리보기 */}
      {tier === "free" && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-6 sm:p-8 shadow-lg border border-gray-200"
          >
            <h3 className="text-lg font-bold text-gray-900 sm:text-xl">
              항목별로 더 들여다보기
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              토니가 각 지표를 어떻게 해석했는지 일부를 열어뒀어요.
            </p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {attributeCards.slice(0, 2).map(card => (
                <div
                  key={card.key}
                  className="rounded-xl border border-gray-100 bg-gray-50/80 p-4"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-800">
                      {card.title}
                    </span>
                    <span
                      className="text-lg font-bold tabular-nums"
                      style={{ color: getScoreColor(card.score) }}
                    >
                      {Math.round(card.score)}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed text-gray-600">
                    {card.comment || "세부 코멘트 준비 중..."}
                  </p>
                </div>
              ))}
            </div>

            <div className="relative mt-4 overflow-hidden rounded-xl border border-dashed border-purple-200">
              <div
                className="pointer-events-none grid select-none gap-3 p-4 opacity-70 blur-[6px] sm:grid-cols-3"
                aria-hidden
              >
                {attributeCards.slice(2).map(card => (
                  <div key={card.key} className="rounded-lg bg-gray-50 p-3">
                    <div className="text-xs font-semibold text-gray-500">
                      {card.title}
                    </div>
                    <div className="mt-1 text-2xl font-bold text-purple-400">
                      {Math.round(card.score)}
                    </div>
                    <div className="mt-2 h-8 rounded bg-gray-200" />
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 px-4 text-center backdrop-blur-[2px]">
                <Sparkles className="h-5 w-5 text-purple-500" />
                <p className="mt-2 text-sm font-medium text-gray-800">
                  나머지 {attributeCards.length - 2}개 항목 코멘트와 차트도
                  준비돼 있어요
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  아래에서 이어서 열어볼 수 있어요
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {tier === "free" && showUpgradeCta && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50/90 via-white to-indigo-50/80 p-5 shadow-sm sm:p-6"
        >
          <div className="flex items-start gap-3">
            <TonyAvatar size="sm" />
            <div className="min-w-0 flex-1">
              <p className="text-base font-semibold text-gray-900">
                토니가 더 준비한 분석이 있어요
              </p>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">
                레퍼런스 아티스트 비교, 전체 항목 코멘트, PDF 리포트까지
                이어서 볼 수 있어요.
                {lowestFocusArea && (
                  <>
                    {" "}
                    특히{" "}
                    <span className="font-medium text-purple-700">
                      {lowestFocusArea.label}
                    </span>{" "}
                    쪽은 더 구체적인 코칭이 궁금하다면 심화 분석이나 프로
                    피드백을 살펴보세요.
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            {onAdvancedAnalysis && (
              <Button
                type="button"
                onClick={() => onAdvancedAnalysis()}
                variant="outline"
                className="border-purple-200 bg-white text-purple-700 hover:bg-purple-50"
              >
                {user?.uid
                  ? "심화 분석 열어보기 · 2,900P"
                  : "가입 후 심화 분석 보기"}
              </Button>
            )}
            <Link
              href="/expert-feedback"
              className="inline-flex items-center justify-center gap-1 px-1 py-2 text-sm font-medium text-gray-600 transition hover:text-gray-900"
            >
              프로에게 직접 물어보기
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>
      )}

      {/* MusicDNA — 분석 흐름 마무리 보너스 */}
      {tier !== "advanced" && result.musicDNA && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#312e81] via-[#4338ca] to-[#7c3aed] text-white shadow-xl"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,#a855f733,transparent_65%)]" />
          <div className="relative flex flex-col gap-6 p-6 sm:flex-row sm:items-center sm:gap-10 sm:p-8">
            <MusicDNAAnimation 
              dnaType={result.musicDNA.key}
              emoji={result.musicDNA.emoji}
              isRevealed={isDNARevealed}
            />
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-[0.3em] text-white/60">
                MUSIC DNA
              </p>
              <AnimatePresence mode="wait">
                {!isDNARevealed ? (
                  <motion.div
                    key="dna-loading"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    className="space-y-3"
                  >
                    <p className="text-sm text-white/70">
                      AI가 당신의 음악 DNA를 분석 중이에요...
                    </p>
                    <div className="flex items-center gap-2 text-white/70">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">잠시만 기다려 주세요</span>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key={`dna-${result.musicDNA.key}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ type: "spring", stiffness: 140, damping: 16 }}
                    className="space-y-3"
                  >
                    <div className="text-sm text-white/70">
                      당신의 음악 DNA는...
                    </div>
                    <h3 className="text-3xl font-bold sm:text-4xl">
                      {result.musicDNA.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-white/85 sm:text-base">
                      {result.musicDNA.description}
                    </p>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70 sm:text-sm">
                      {result.musicDNA.tagline}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}

      {/* AI 심화 분석 결과 - 심화 분석 버전만 표시 */}
      {tier === "advanced" && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-3xl p-6 sm:p-8 shadow-xl border-2 border-purple-200"
        id="advanced-analysis-report"
      >
        {/* 헤더 - 심화 분석 결과 타이틀 */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b-2 border-purple-300/30">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold uppercase tracking-wider rounded-full mb-3">
              <Sparkles className="w-4 h-4" />
              심화 분석 결과
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              AI 심화 분석 리포트
            </h2>
            <p className="text-gray-600 text-base sm:text-lg">
              {new Date().toLocaleDateString("ko-KR", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })} · 종합 분석 완료
            </p>
          </div>
          {/* PDF 저장 버튼 */}
          <Button
            onClick={async () => {
              try {
                toast.loading("PDF 생성 중...");
                const { generateAdvancedAnalysisPDF } = await import("@/lib/utils/advanced-analysis-pdf");
                const blob = await generateAdvancedAnalysisPDF(result, user?.displayName || null);
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `심화분석_${new Date().toISOString().split("T")[0]}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.dismiss();
                toast.success("PDF가 다운로드되었습니다!");
              } catch (error) {
                console.error("PDF 생성 오류:", error);
                toast.dismiss();
                toast.error("PDF 생성 중 오류가 발생했습니다.");
              }
            }}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            PDF 저장
          </Button>
        </div>

            {/* 3개 차트 그리드 - 세련된 디자인 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-8">
              {/* 감정 곡선 차트 */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-6 sm:p-7 border border-indigo-100 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">
                    감정 표현 곡선
                  </h4>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart
                    data={Array.from({ length: 7 }, (_, i) => {
                      // 결정적 패턴 생성 (파일/결과 기반 시드)
                      // overallScore와 emotionalExpression을 시드로 사용
                      const seed = (result.overallScore + result.emotionalExpression) % 1000;
                      // 결정적 파형 생성 (같은 결과는 같은 패턴)
                      const phase = (i * 0.8 + seed * 0.01) % (Math.PI * 2);
                      const variation = Math.sin(phase) * 0.25; // -0.25 ~ +0.25 변동
                      const baseIntensity = result.emotionalExpression / 100;
                      const intensity = Math.max(0, Math.min(100, (baseIntensity + variation) * 100));
                      
                      return {
                        time: `${Math.floor(i * 0.5)}:${String(
                          (i * 30) % 60
                        ).padStart(2, "0")}`,
                        intensity,
                      };
                    })}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="time"
                      tick={{ fill: "#6B7280", fontSize: 11 }}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fill: "#6B7280", fontSize: 11 }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        color: "#1F2937",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="intensity"
                      stroke="#3B82F6"
                      strokeWidth={3}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </motion.div>

              {/* 리듬 안정성 원형 게이지 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-white rounded-2xl p-6 sm:p-7 flex flex-col items-center justify-center border border-blue-100 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center gap-3 mb-5 w-full">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <Music className="w-5 h-5 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">
                    리듬 안정성
                  </h4>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <RadialBarChart
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="90%"
                    data={[
                      {
                        name: "리듬 안정성",
                        value: result.rhythmStability,
                        fill: "#3B82F6",
                      },
                    ]}
                    startAngle={90}
                    endAngle={-270}
                  >
                    <RadialBar
                      dataKey="value"
                      cornerRadius={10}
                      fill="#3B82F6"
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
              </motion.div>

              {/* 음색 균형 막대 그래프 */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl p-6 sm:p-7 border border-orange-100 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
                    <Waves className="w-5 h-5 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">
                    음색 균형
                  </h4>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={[
                      {
                        name: "저음",
                        value: Math.min(
                          60,
                          (result.toneConsistency * 0.7 +
                            result.energyDynamics * 0.3) *
                            0.6
                        ),
                      },
                      {
                        name: "중음",
                        value: Math.min(
                          60,
                          (result.toneConsistency * 0.8 +
                            result.energyDynamics * 0.2) *
                            0.67
                        ),
                      },
                      {
                        name: "고음",
                        value: Math.min(
                          60,
                          (result.toneConsistency * 0.5 +
                            result.energyDynamics * 0.5) *
                            0.33
                        ),
                      },
                    ]}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" domain={[0, 60]} hide />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tick={{ fill: "#6B7280", fontSize: 11 }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        color: "#1F2937",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                      {[
                        { name: "저음", color: "#3B82F6" },
                        { name: "중음", color: "#6B7280" },
                        { name: "고음", color: "#F97316" },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </motion.div>
            </div>

            {/* 주요 통찰 카드들 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-5 sm:p-6 border border-pink-100"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2 text-base">감정 톤</h5>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                      이 곡의 감정적 여정은 명확한 상승 곡선을 보이며 청중에게
                      긍정적인 경험을 선사합니다.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 sm:p-6 border border-blue-100"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Waves className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2 text-base">
                      리듬 구조
                    </h5>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                      복잡한 리듬 패턴 속에서도 일관성을 유지하여 몰입감을
                      높이고 안정적인 청취 경험을 제공합니다.
                    </p>
                  </div>
                </div>
              </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-5 sm:p-6 border border-amber-100"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2 text-base">
                      사운드 다양도
                    </h5>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                      다양한 악기와 사운드 레이어링을 통해 풍부한 음향적 질감을
                      구현, 다채로운 청각적 경험을 선사합니다.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
      )}

      {/* AI 코치 코멘트 섹션 (유료 버전만) - AI 점수 아래로 이동 */}
      {tier === "advanced" && result.comments.coachComment && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-6 sm:p-8 shadow-lg border border-gray-200"
        >
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Heart className="w-6 h-6 text-pink-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                AI 코치의 따뜻한 메시지
              </h3>
              <p className="text-gray-600 text-sm">
                당신의 음악에 담긴 감성을 느꼈어요
              </p>
            </div>
          </div>

          {/* 감성 코멘트 */}
          <div className="mb-4">
            <TypingBubble 
              text={result.comments.coachComment || ""} 
              delay={0.4}
            />
          </div>

          {/* 다음 단계 추천 */}
          {result.comments.nextStep && (
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-base sm:text-lg mb-2 text-gray-900">다음 단계 추천</h4>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                    {result.comments.nextStep}
                  </p>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}


      {/* 유료 버전 전용 섹션들 */}
      {tier === "advanced" && (
        <>

          {/* Radar Chart (데스크탑) + Bar Chart (모바일) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
          >
            <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Music className="w-6 h-6 text-purple-600" />
              종합 분석 차트
            </h3>

            {/* 데스크탑: Radar Chart */}
            <div className="hidden md:block">
              <ResponsiveContainer width="100%" height={400}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E5E7EB" />
                  <PolarAngleAxis
                    dataKey="category"
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                  />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} />
                  <Radar
                    name="점수"
                    dataKey="score"
                    stroke="#7E57C2"
                    fill="#7E57C2"
                    fillOpacity={0.6}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* 모바일: Bar Chart */}
            <div className="block md:hidden">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                    {barData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={getScoreColor(entry.score)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {spotifyDetails && similarArtists.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-purple-100"
            >
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-purple-600" />
                당신과 어울리는 아티스트
              </h3>

              <p className="text-sm text-gray-600 mb-6">
                ConnectTone 레퍼런스 라이브러리와 Spotify 메타데이터를 결합해
                계산한 유사도입니다. 비슷한 결의 아티스트를 참고해서 사운드를
                확장해 보세요.
              </p>

              <div className="overflow-x-auto pb-2">
                <div className="flex gap-6 min-w-max">
                  {similarArtists.map((artist, index) => {
                    const percent = Math.round(artist.similarity * 1000) / 10;
                    const safePercent = Math.max(0, Math.min(100, percent));
                    const initials = artist.artist
                      .split(" ")
                      .map(word => word[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase();

                    return (
                      <div
                        key={`${artist.artist}-${index}`}
                        className="relative w-64 flex-shrink-0 rounded-2xl bg-white p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                      >
                        <div className="absolute top-4 right-6 text-sm font-semibold text-gray-400">
                          #{index + 1}
                        </div>

                        <div className="flex items-center gap-4 mb-6">
                          <div className="w-14 h-14 rounded-xl bg-blue-100 text-blue-600 font-bold flex items-center justify-center text-xl shadow-md">
                            {initials || "♪"}
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide text-gray-500">
                              {artist.genre}
                            </p>
                            <h4 className="text-xl font-bold leading-tight text-gray-900">
                              {artist.artist}
                            </h4>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-baseline justify-between">
                            <span className="text-sm text-gray-600">
                              유사도 지수
                            </span>
                            <span className="text-2xl font-bold text-blue-600">
                              {safePercent.toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full h-2 rounded-full bg-gray-200">
                            <div
                              className="h-full rounded-full bg-blue-500"
                              style={{ width: `${safePercent}%` }}
                            />
                          </div>
                        </div>

                        {artist.externalUrl && (
                          <a
                            href={artist.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-6 inline-flex items-center justify-center w-full text-sm font-semibold rounded-lg bg-blue-600 text-white py-2 hover:bg-blue-700 transition"
                          >
                            Spotify 아티스트 열기 →
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* 항목별 분석 카드 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-5"
          >
            {attributeCards.map((card, index) => {
              const barColor = getScoreColor(card.score);
              return (
                <motion.div
                  key={card.key}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: card.delay }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100/70 px-6 py-6 sm:px-8 sm:py-7"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
                    <div className="flex items-center gap-4 md:min-w-[220px]">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-500 font-semibold text-lg">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-lg sm:text-xl font-semibold text-gray-900">
                          {card.title}
                        </p>
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="w-full bg-gray-100 rounded-full h-2.5 sm:h-3 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${card.score}%` }}
                          transition={{ duration: 1, delay: card.delay + 0.1 }}
                          className="h-full rounded-full"
                          style={{ backgroundColor: barColor }}
                        />
                      </div>
                      <p className="mt-3 text-sm sm:text-base text-gray-700 leading-relaxed">
                        {card.comment}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* 🎯 전문가 분석: 구체적 개선 포인트 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-amber-200 shadow-lg"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">전문가 분석: 개선 포인트</h3>
                <p className="text-sm text-gray-500">집중적으로 연습하면 빠르게 성장할 수 있는 부분이에요</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* 점수가 낮은 순서대로 개선점 표시 */}
              {[
                { label: "음정 정확도", score: result.pitchAccuracy, tip: "음정 연습 앱으로 하루 10분씩 연습해보세요. 피아노 음과 함께 따라 부르면 효과적이에요." },
                { label: "리듬 안정성", score: result.rhythmStability, tip: "메트로놈을 켜고 천천히 박자 맞추기 연습을 해보세요. BPM 60부터 시작하는 것을 추천해요." },
                { label: "표현력", score: result.emotionalExpression, tip: "가사의 감정을 먼저 이해하고, 강약 조절에 신경 써보세요. 녹음해서 들어보면 도움이 돼요." },
                { label: "톤 일관성", score: result.toneConsistency, tip: "호흡 연습이 핵심이에요. 복식호흡으로 일정한 공기량을 유지하는 연습을 해보세요." },
              ]
                .sort((a, b) => a.score - b.score)
                .slice(0, 2) // 가장 낮은 2개만
                .map((item, idx) => (
                  <div key={item.label} className="bg-amber-50 rounded-xl p-5 border border-amber-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <span className="font-semibold text-gray-900">{item.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-amber-600">{item.score}</span>
                        <span className="text-sm text-gray-500">/ 100</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed pl-8">
                      💡 <strong>전문가 팁:</strong> {item.tip}
                    </p>
                  </div>
                ))}
            </div>
          </motion.div>

          {/* AI 추천 연습 루틴 */}
          {result.recommendations.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 border border-purple-100"
            >
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-purple-600" />
                AI 추천 연습 루틴
              </h3>

              <div className="space-y-4">
                {result.recommendations.map((rec, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="bg-white rounded-xl p-6 shadow-md"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-purple-600 font-bold">
                          {index + 1}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-2">
                          {rec.title}
                        </h4>
                        <p className="text-gray-600 text-sm mb-3">
                          {rec.description}
                        </p>

                        {/* 연습곡 추천 */}
                        {rec.practiceSongs && rec.practiceSongs.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-gray-700 mb-2">
                              🎵 추천 연습곡:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {rec.practiceSongs.map((song, songIndex) => (
                                <span
                                  key={songIndex}
                                  className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
                                >
                                  {song}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {rec.link && (
                          <a
                            href={rec.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-purple-600 hover:text-purple-700 text-sm font-medium"
                          >
                            튜토리얼 보기 →
                          </a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* 참고 사항 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="bg-blue-50 border border-blue-200 rounded-xl p-6"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">
                  분석 결과 안내
                </h4>
                <p className="text-sm text-blue-800">
                  이 분석 결과는 AI가 자동으로 생성한 것으로, 참고용으로
                  활용해주세요. 더 정확한 피드백이 필요하시면 전문가 분석을
                  신청하실 수 있습니다.
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
      </>
      )}
    </div>
  );
}
